import { type VFC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMatch, useNavigate, useParams } from "react-router";

import { LoadingSpinner } from "widgets";
import { toSafeInt } from "widgets/utils";

import styleLayout from "../core/styles/PageLayout.module.scss";

import { useDocumentTitle } from "../../app-utils/hooks/useDocumentTitle.js";
import { useURLViewID } from "../../app-utils/hooks/useUrl.js";
import { type InputTableQuerySpec } from "../../graphql.js";
import {
  type IDatasetViewerState,
  clearDatasetViewer,
  getTableDataFromGroupId,
  loadGroupDataset,
} from "../../redux/modules/dataset-viewer.js";
import { type ISavedGroupState } from "../../redux/modules/saved-group.js";
import { type SavedViewState } from "../../redux/modules/saved-view.js";
import { getTableQuerySpecWithSelection } from "../../redux/modules/table-data.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import { ButtonHeader } from "../core/ButtonHeader.js";
import { ViewDataViewerLayoutButtons } from "../core/ViewPaneLayoutButtons.js";
import GenericDataTableContainer from "../core/grid/GenericDataTableContainer.js";
import { AnalyticalDataButtonHeader } from "../core/navigation-panel/ButtonHeaderHoc.js";
import { ContextWidgetType, type INavigationContextProps } from "../core/navigation-panel/ContextWidget.js";
import { type INavPanelCategory } from "../core/navigation-panel/NavigationPanel.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import PageLayout from "../core/navigation-panel/PageLayout.js";
import DatasetViewer from "./DatasetViewer.js";
import { DATATABLE_ID_TYPE, DATA_VIEWER_MODE, ENTITY_TYPE, UNSPECIFIED_ENTITY_ID, VIEW } from "@/settings/settings.js";
import { GROUP_ENTITY } from "@/ui-modules/core/save-dialog/utils.ts";

const DataViewerPage: VFC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const navigate = useNavigate();
  useDocumentTitle("Analytical Data View");
  const { groupId, datasetId } = useParams<"groupId" | "datasetId">();
  const selectionGroupMatch = useMatch("/visualizer/data/group/:groupId/table/:selectionGroupId");
  const viewId = useURLViewID();
  const { view, isLoading: isSavedViewLoading } = useSelector<IAppState, SavedViewState>((state) => state.savedView);
  const { group, isLoading } = useSelector<IAppState, ISavedGroupState>((state) => state.savedGroup);
  const { mode, tableSelectionSpec } = useSelector<IAppState, IDatasetViewerState>((state) => state.datasetViewer);

  useEffect(() => {
    if (groupId) {
      dispatch(getTableDataFromGroupId(Number.parseInt(groupId))).then((tableData) => {
        if (!viewId) {
          // if the view provides all the information there is no need to get the dataset
          dispatch(loadGroupDataset(getTableQuerySpecWithSelection(tableData)));
        }
        // else the saved group has been loaded as part of the 'getTableDataFromGroupId' call, so it is complete
      });
    }
  }, [dispatch, groupId]);

  // cleanup at this level ; cleaning up in the DataViewer component would reset the state when reaching sub pages
  useEffect(() => {
    return () => {
      dispatch(clearDatasetViewer());
    };
  }, []);

  const getContextProps = (): INavigationContextProps => {
    if (viewId && !isSavedViewLoading && Number.parseInt(view?.id) === viewId) {
      return {
        type: ContextWidgetType.SAVED_VIEW,
        data: view,
      };
    } else if (groupId && !isLoading && group) {
      return {
        type: ContextWidgetType.SAVED_GROUP,
        data: group,
      };
    }

    return null;
  };

  const renderChartDataTableFromSelection = (selectionGroupId: number) => {
    const tableSpec: InputTableQuerySpec = {
      id: selectionGroupId,
      idType: DATATABLE_ID_TYPE.GROUP,
      queryType: GROUP_ENTITY, // remove ?
      resultType: group?.type,
    };

    // button header should perhaps propose saving a DATA_VIEW ? but that would mean
    // - need a way to get back from the full-screen table to chart
    // - leads to issues of having a DATA_VIEW presenting a TABLE_VIEW... => inconsistencies !
    const buttonHeader = (
      <ButtonHeader
        viewType={VIEW.TABLE_VIEW.type}
        tableSpec={tableSpec}
        getParentEntityName={() => ENTITY_TYPE.SAMPLE}
        hideBlastBtn={true}
        hideExportBtn={true}
      />
    );

    return group ? (
      <GenericDataTableContainer
        title="Chart table selection"
        tableSpec={tableSpec}
        rightToolbarElement={buttonHeader}
        viewId={viewId}
      />
    ) : (
      <LoadingSpinner inCenter />
    );
  };

  const getNavPanelCategories = (): INavPanelCategory[] => {
    const categories: INavPanelCategory[] = [
      {
        label: "Analytical Data",
        navItems: [
          {
            label: mode === DATA_VIEWER_MODE.GROUP ? "By Selection" : "By Dataset",
            route: ``,
            exact: true,
            pageComponent: (
              <>
                <PageHeader
                  title={mode === DATA_VIEWER_MODE.GROUP ? "Sample Data" : "Analytical Data"}
                  actionWidgets={[<AnalyticalDataButtonHeader key={"data-viewer-page-action-1"} />]}
                  widgets={[mode === DATA_VIEWER_MODE.GROUP && <ViewDataViewerLayoutButtons />]}
                />

                <div className={styleLayout.page}>
                  <DatasetViewer
                    genomeId={UNSPECIFIED_ENTITY_ID}
                    handleDatasetChanged={(datasetId) => {
                      navigate(`/visualizer/data/dataset/${datasetId}`);
                    }}
                    viewId={viewId}
                    groupId={toSafeInt(groupId)}
                    groupTableSelectionSpec={tableSelectionSpec}
                    datasetId={toSafeInt(datasetId)}
                  />
                </div>
              </>
            ),
          },
        ],
      },
    ];

    // check if we have a selectionGroupId
    if (selectionGroupMatch?.params.selectionGroupId) {
      const subSelectionCategory: INavPanelCategory = {
        label: "Samples",
        navItems: [
          {
            label: "Sub-selection",
            route: `table/${selectionGroupMatch?.params.selectionGroupId}`,
            pageComponent: renderChartDataTableFromSelection(
              Number.parseInt(selectionGroupMatch?.params.selectionGroupId),
            ),
          },
        ],
      };
      categories.push(subSelectionCategory);
    }

    return categories;
  };

  return (
    <PageLayout contextWidgetProps={getContextProps()} navPanelCategories={getNavPanelCategories()} navPanelCollapsed />
  );
};

export default DataViewerPage;
